import type { LoadListRow } from "./api";
import { formatAmount, formatMoveType, formatWeightLb } from "./format";
import { sumPricedAmount } from "./selectors";

// Pure display logic for the Daily / Weekly Loads Report. Everything a
// driver reads on that screen is derived here so it can be unit-tested
// headlessly (same pattern as ./selectors.ts for the home list).
//
// TIME: a load's business day is terminal-local, NOT device-local. A
// driver in a different timezone (or a CI box on UTC) must still see the
// ticket on the day the terminal wrote it, so every date here is resolved
// through TERMINAL_TZ explicitly — this module never leans on the
// ambient timezone.
//
// SETTLEMENT WEEK: verified against real Evans settlements — the
// "Originating Week" IS the ISO week (Monday 00:00 → Sunday 23:59
// terminal-local) and it is settled the FOLLOWING TUESDAY (Tue Aug 11
// 2026 settled Aug 3–9; Tue Jul 28 2026 settled Jul 20–26). Bucketing by
// ISO week makes the driver's weekly window equal the settlement window
// one-for-one, which is the whole point of the report: dispatch verifies
// this total against the settlement before payday.
export const TERMINAL_TZ = "America/New_York";

// How the terminal's timezone is written to a driver. Deliberately the
// stable "ET" rather than the Intl abbreviation, which is EDT for half
// the year and EST for the other half: a scheduled send is read as a
// standing arrangement ("Wednesdays at 8"), and a label that changed
// twice a year would read as the time itself having moved.
export const TERMINAL_TZ_LABEL = "ET";

// A business day, as "YYYY-MM-DD" in terminal-local time. Passing these
// around as civil-date strings rather than Dates keeps the arithmetic
// below (week starts, day offsets) free of any offset math.
export type DayKey = string;

const DAY_KEY_RE = /^\d{4}-\d{2}-\d{2}$/;

const TERMINAL_PARTS = new Intl.DateTimeFormat("en-US", {
  timeZone: TERMINAL_TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

// Day keys are formatted by anchoring them at UTC midnight and rendering
// in UTC — the key is already terminal-local, so re-applying a timezone
// would shift it a second time.
const WEEKDAY_FMT = new Intl.DateTimeFormat("en-US", {
  timeZone: "UTC",
  weekday: "short",
  month: "short",
  day: "numeric",
});
const SHORT_FMT = new Intl.DateTimeFormat("en-US", {
  timeZone: "UTC",
  month: "short",
  day: "numeric",
});

function keyFromInstant(d: Date): DayKey {
  const parts = TERMINAL_PARTS.formatToParts(d);
  const get = (type: string): string =>
    parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

// Resolve anything a caller might hold — an ISO timestamp from the API, a
// Date, or an already-resolved day key — to a terminal-local day key.
// Returns null for absent/unparseable input; callers decide what to show.
//
// A bare "YYYY-MM-DD" is passed through untouched on purpose: `new
// Date("2025-08-18")` is UTC midnight, which is Aug 17 in New York, so
// round-tripping it through the timezone would move the day backwards.
export function toDayKey(value: string | Date | null | undefined): DayKey | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "string" && DAY_KEY_RE.test(value)) return value;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return keyFromInstant(d);
}

// The business day "now" falls on, terminal-local.
export function todayKey(now: Date = new Date()): DayKey {
  return keyFromInstant(now);
}

function keyToUtc(key: DayKey): Date {
  const [y, m, d] = key.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d));
}

export function addDays(key: DayKey, days: number): DayKey {
  const d = keyToUtc(key);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

// Monday of the ISO week containing `key`.
export function isoWeekStart(key: DayKey): DayKey {
  const dow = keyToUtc(key).getUTCDay(); // 0 = Sunday
  return addDays(key, -((dow + 6) % 7)); // Monday = 0 days back
}

// The Tuesday a given ISO week settles: two days after its Sunday, i.e.
// eight days after its Monday. (Week of Aug 18–24 2025 → Tue Aug 26.)
export function settlesTuesdayFor(weekStart: DayKey): DayKey {
  return addDays(weekStart, 8);
}

// "Mon, Aug 18"
export function formatDayKey(key: DayKey): string {
  return WEEKDAY_FMT.format(keyToUtc(key));
}

// "Aug 18"
export function formatDayKeyShort(key: DayKey): string {
  return SHORT_FMT.format(keyToUtc(key));
}

// Which window the report is showing. The two views navigate in
// different units — a day at a time, or a settlement week at a time.
export type ReportView = "daily" | "weekly";

// Move the report window `delta` steps (-1 back, +1 forward). In Daily a
// step is one day; in Weekly it is a whole ISO week, and the anchor
// lands on that week's MONDAY so repeated steps can't drift — stepping
// from a Sunday would otherwise skip a week the first time and not the
// next. Month and year boundaries fall out of the day arithmetic, which
// is UTC-anchored on the civil date and so never off by an hour.
export function shiftReportAnchor(
  view: ReportView,
  anchor: DayKey,
  delta: number,
): DayKey {
  return view === "daily"
    ? addDays(anchor, delta)
    : addDays(isoWeekStart(anchor), delta * 7);
}

// One load as the report renders it. Only fields the parser actually
// produces today: `pin` and pickup time have no extractor yet, so they
// are omitted rather than rendered blank — they can be added later
// without changing this shape.
export interface ReportLoadRow {
  id: number;
  href: string;
  day: DayKey;
  containerLabel: string;
  moveTypeLabel: string | null;
  carrierLabel: string | null;
  weightLabel: string | null;
  // The EIR number written on the ticket — dispatch's cross-reference
  // back to the paper when a line is queried.
  ticketLabel: string | null;
  dateLabel: string;
  amountLabel: string | null;
  needsAmount: boolean;
}

export interface DailyLoadsReport {
  day: DayKey;
  dayLabel: string;
  rows: ReportLoadRow[];
  count: number;
  // Priced loads only, as a number; totalLabel is the same value
  // formatted. Unpriced loads are NOT zero-valued into the total — they
  // are counted by needAmountCount so the driver can see the total is
  // incomplete rather than silently wrong (same rule as the home list).
  total: number;
  totalLabel: string;
  needAmountCount: number;
}

export interface WeeklyDayBucket {
  day: DayKey;
  dayLabel: string;
  count: number;
  subtotal: number;
  subtotalLabel: string;
  needAmountCount: number;
}

export interface WeeklyLoadsReport {
  weekStart: DayKey;
  weekEnd: DayKey;
  settlesTuesday: DayKey;
  // "Aug 18 – Aug 24"
  rangeLabel: string;
  // "Tue, Aug 26"
  settlesLabel: string;
  // Exactly seven buckets, Monday → Sunday. Days with no loads are kept:
  // a blank Wednesday in a settlement week is information, not noise.
  days: WeeklyDayBucket[];
  count: number;
  total: number;
  totalLabel: string;
  needAmountCount: number;
}

function toReportRow(row: LoadListRow, day: DayKey): ReportLoadRow {
  const amountLabel = formatAmount(row.amount);
  return {
    id: row.id,
    href: `/l/${row.id}`,
    day,
    containerLabel: row.containerNo ?? "—",
    moveTypeLabel: formatMoveType(row.moveType),
    carrierLabel: row.carrierName ?? row.carrierCode,
    weightLabel: formatWeightLb(row.grossWeightLb),
    ticketLabel: row.eirNumber,
    dateLabel: formatDayKey(day),
    amountLabel,
    needsAmount: amountLabel === null,
  };
}

function needsAmount(row: LoadListRow): boolean {
  return formatAmount(row.amount) === null;
}

// Money formatting never fails for a finite number, but the label type is
// nullable at the edges; totals always render, so pin the fallback here.
function money(n: number): string {
  return formatAmount(n.toFixed(2)) ?? "$0.00";
}

// Every load in `loads`, keyed by the terminal-local business day it
// falls on. Loads with an unreadable loadDate are dropped — they belong
// to no day, and inventing one would put money on the wrong report.
function groupByDay(loads: LoadListRow[]): Map<DayKey, LoadListRow[]> {
  const byDay = new Map<DayKey, LoadListRow[]>();
  for (const row of loads) {
    const day = toDayKey(row.loadDate);
    if (day === null) continue;
    const bucket = byDay.get(day);
    if (bucket) bucket.push(row);
    else byDay.set(day, [row]);
  }
  return byDay;
}

// One business day's loads, in the order given (the server sorts
// reverse-chron), with that day's total.
export function buildDailyLoadsRows(
  loads: LoadListRow[] | null | undefined,
  day: string | Date | null | undefined,
): DailyLoadsReport {
  const dayKey = toDayKey(day) ?? todayKey();
  const forDay = (loads ?? []).filter((row) => toDayKey(row.loadDate) === dayKey);
  const total = sumPricedAmount(forDay);

  return {
    day: dayKey,
    dayLabel: formatDayKey(dayKey),
    rows: forDay.map((row) => toReportRow(row, dayKey)),
    count: forDay.length,
    total,
    totalLabel: money(total),
    needAmountCount: forDay.filter(needsAmount).length,
  };
}

// The ISO (Monday–Sunday) settlement week containing `anchorDate`,
// bucketed per day. `anchorDate` may be any day in the week — the week is
// derived from it, so "today" is a valid anchor every day of the week.
export function buildWeeklyLoadsRows(
  loads: LoadListRow[] | null | undefined,
  anchorDate: string | Date | null | undefined,
): WeeklyLoadsReport {
  const anchor = toDayKey(anchorDate) ?? todayKey();
  const weekStart = isoWeekStart(anchor);
  const weekEnd = addDays(weekStart, 6);
  const byDay = groupByDay(loads ?? []);

  const days: WeeklyDayBucket[] = [];
  let count = 0;
  let total = 0;
  let needAmountCount = 0;

  for (let i = 0; i < 7; i++) {
    const day = addDays(weekStart, i);
    const rows = byDay.get(day) ?? [];
    const subtotal = sumPricedAmount(rows);
    count += rows.length;
    total += subtotal;
    const dayNeeds = rows.filter(needsAmount).length;
    needAmountCount += dayNeeds;

    days.push({
      day,
      dayLabel: formatDayKey(day),
      count: rows.length,
      subtotal,
      subtotalLabel: money(subtotal),
      needAmountCount: dayNeeds,
    });
  }

  return {
    weekStart,
    weekEnd,
    settlesTuesday: settlesTuesdayFor(weekStart),
    rangeLabel: `${formatDayKeyShort(weekStart)} – ${formatDayKeyShort(weekEnd)}`,
    settlesLabel: formatDayKey(settlesTuesdayFor(weekStart)),
    days,
    count,
    total,
    totalLabel: money(total),
    needAmountCount,
  };
}
